import './myseasons.css'

import {
	IonButton,
	IonIcon,
	IonInput,
	IonItem,
	IonLabel,
	IonSelect,
	IonSelectOption,
} from '@ionic/react'
import {
	checkmarkSharp,
	closeOutline,
	cloudDoneOutline,
	createOutline,
} from 'ionicons/icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import Connector from './Connector'
import {
	useNewSeasonConnectorState,
	useNewTeamId,
	useSection,
	useTeamRosters,
} from './data'
import {
	seasonGameFormatOptions,
	seasonNumPeriodsOptions,
	seasonPrivacyOptions,
	SEASON_RULE_STANDARD,
	seasonRulesOptions,
} from './definitions'
import netballConnectImage from './media/netball-connect.png'
import playHqImage from './media/playhq.png'
import { useScrollIntoView } from './useScrollIntoView'

type SeasonEditorProps = {
	mode: 'add' | 'edit'
	season: Partial<Season>
	onClose: () => void
	className?: string
	scrollTargetRef?: React.RefObject<HTMLElement | null>
	onSubmit?: (season: Season) => void
	onSavePatch?: (patch: Partial<Season> & { id: SeasonId }) => Promise<void>
	connectorMode?: 'hidden' | 'draft' | 'current'
	autoFocusName?: boolean
	submitButtonContent?: React.ReactNode
	cancelButtonContent?: React.ReactNode
}

export const SeasonEditor = (props: SeasonEditorProps) => {

	const {
		mode,
		season,
		onClose,
		className,
		scrollTargetRef,
		onSubmit,
		onSavePatch,
		connectorMode = 'hidden',
		autoFocusName = false,
		submitButtonContent,
		cancelButtonContent,
	} = props

	const [ draft, setDraft ] = useState<Partial<Season>>(() => applySeasonDefaults(season))
	const [ editingName, setEditingName ] = useState(() => {
		const initialSeason = applySeasonDefaults(season)
		return mode === 'add' && autoFocusName && !initialSeason.name?.trim()
	})
	const [ draftName, setDraftName ] = useState(() => applySeasonDefaults(season).name || '')
	const [ savedDraftName, setSavedDraftName ] = useState(() => applySeasonDefaults(season).name || '')
	const [ saveIndicatorKey, setSaveIndicatorKey ] = useState(0)
	const [ haveSavedChanges, setHaveSavedChanges ] = useState(false)

	const containerRef = useRef<HTMLDivElement>(null)
	const nameInputRef = useRef<HTMLIonInputElement>(null)
	const connectorOpenRef = useRef<(() => void) | null>(null)
	const nameEditActionRef = useRef<'save' | 'cancel' | null>(null)
	const nameBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const newTeamId = useNewTeamId()
	const { team } = useSection()

	const effectiveTeamId = newTeamId || team
	const rosterId = `${effectiveTeamId || 'unknown'}__${season.id || 'new'}`
	const rosters = useTeamRosters(rosterId)
	const haveRosters = mode === 'edit' && !!season.id && Object.values(rosters || { }).some(obj => obj.roster?.length > 0)

	useEffect(() => {
		if (mode === 'add') {
			return
		}

		const nextDraft = applySeasonDefaults(season)
		setDraft(nextDraft)
		if (!editingName) {
			setDraftName(nextDraft.name || '')
			setSavedDraftName(nextDraft.name || '')
		}
	}, [ mode, season, editingName ])

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>
		if (editingName) {
			timer = setTimeout(() => nameInputRef.current?.setFocus(), 80)
		}
		return () => clearTimeout(timer)
	}, [ editingName ])

	useEffect(() => {
		return () => {
			if (nameBlurTimerRef.current) {
				clearTimeout(nameBlurTimerRef.current)
			}
		}
	}, [])

	const scrollRef = (scrollTargetRef || containerRef) as React.RefObject<HTMLElement | null>

	useScrollIntoView(scrollRef)

	const canSubmit = !!draft.name?.trim()
	const showConnector = connectorMode !== 'hidden'
	const connectorIsCurrent = connectorMode === 'current'

	function updateDraftField<K extends keyof Season>(field: K, value: Season[K]) {
		setDraft(prev => ({
			...prev,
			[field]: value,
		}))
	}

	const flashSaveIndicator = useCallback(() => {
		setHaveSavedChanges(true)
		setSaveIndicatorKey(prev => prev + 1)
	}, [])

	const handleDraftConnectorInput = useCallback((event: { target: { value: Connector | null } }) => {
		setDraft(prev => applyConnectorToSeason(prev, event.target.value))
	}, [])

	async function saveField<K extends keyof Season>(field: K, value: Season[K]) {
		if (mode === 'add') {
			updateDraftField(field, value)
			return
		}

		if (!draft.id || !onSavePatch) {
			return
		}

		const previousValue = draft[field]
		if (Object.is(previousValue, value)) {
			return
		}

		updateDraftField(field, value)

		try {
			await onSavePatch({
				id: draft.id,
				[field]: value,
			} as Partial<Season> & { id: SeasonId })
			flashSaveIndicator()
		} catch (err) {
			setDraft(prev => ({
				...prev,
				[field]: previousValue,
			}))
			throw err
		}
	}

	const saveName = async () => {
		const trimmedName = draftName.trim()
		if (!trimmedName) {
			return
		}

		if (mode === 'add') {
			updateDraftField('name', trimmedName)
			setSavedDraftName(trimmedName)
			setEditingName(false)
			return
		}

		if (!draft.id || !onSavePatch) {
			return
		}

		const previousName = draft.name

		try {
			await saveField('name', trimmedName)
			setSavedDraftName(trimmedName)
			setEditingName(false)
		} catch (err) {
			setDraftName(previousName || '')
			throw err
		}
	}

	const startEditingName = () => {
		setSavedDraftName(draft.name || '')
		setDraftName(draft.name || '')
		setEditingName(true)
	}

	const cancelEditingName = () => {
		setDraftName(savedDraftName)
		if (mode === 'add') {
			updateDraftField('name', savedDraftName as Season['name'])
		}
		setEditingName(false)
	}

	const finishEditingName = async (action: 'save' | 'cancel' | 'auto') => {
		if (nameBlurTimerRef.current) {
			clearTimeout(nameBlurTimerRef.current)
			nameBlurTimerRef.current = null
		}

		const nextAction = action === 'auto'
			? (draftName.trim() ? 'save' : 'cancel')
			: action

		nameEditActionRef.current = null

		if (nextAction === 'cancel') {
			cancelEditingName()
			return
		}

		await saveName()
	}

	const handleNameBlur = () => {
		if (nameBlurTimerRef.current) {
			clearTimeout(nameBlurTimerRef.current)
		}

		nameBlurTimerRef.current = setTimeout(() => {
			void finishEditingName(nameEditActionRef.current || 'auto').catch(() => {})
		}, 0)
	}

	const onSubmitAdd = () => {
		if (!canSubmit || !onSubmit) {
			return
		}
		onSubmit(draft as Season)
	}

	const myClasses = [ 'season-settings-panel' ]
	if (className) {
		myClasses.push(className)
	}

	return (
		<div ref={containerRef} className={myClasses.join(' ')}>
			<div className="season-settings-header">
				<div className="season-settings-header-text">
                    {
                        mode === 'add' ? 'New Season' : 'Season Settings'
                    }
				</div>
				<div className="season-settings-header-actions">
					{
						mode === 'edit' && haveSavedChanges ? (
							<span
								key={saveIndicatorKey}
								className="season-settings-save-indicator"
								aria-hidden="true"
							>
								<IonIcon icon={checkmarkSharp} />
							</span>
						) : null
					}
					<IonButton
						className="button-cancel"
						onClick={onClose}
						fill="clear"
						color="medium"
					>
						{cancelButtonContent || (mode === 'add' ? 'Cancel' : 'Close')}
					</IonButton>
				</div>
			</div>
			<SeasonSettingRow
				label="Name"
				onClick={!editingName ? startEditingName : undefined}
			>
				{
					editingName ? (
						<form
							className="season-setting-name-editor"
							onSubmit={event => {
									event.preventDefault()
									void finishEditingName('auto').catch(() => {})
								}}
							>
								<IonInput
									ref={nameInputRef}
									className="season-setting-name-input"
									value={draftName}
									aria-label="Season name"
									onIonBlur={handleNameBlur}
									onIonInput={event => {
										const nextName = String(event.detail.value ?? '')
										setDraftName(nextName)
										if (mode === 'add') {
											updateDraftField('name', nextName as Season['name'])
										}
									}}
								/>
								<IonButton
								type="submit"
								fill="clear"
								size="small"
								disabled={!draftName.trim()}
								onPointerDown={() => {
									nameEditActionRef.current = 'save'
								}}
							>
								<IonIcon icon={checkmarkSharp} />
							</IonButton>
							<IonButton
								type="button"
								fill="clear"
								size="small"
								color="medium"
								onPointerDown={() => {
									nameEditActionRef.current = 'cancel'
								}}
								onClick={() => {
									void finishEditingName('cancel').catch(() => {})
								}}
							>
								<IonIcon icon={closeOutline} />
							</IonButton>
						</form>
					) : (
						<button
							type="button"
							className={`season-setting-value-button ${draft.name ? '' : 'season-setting-no-name'}`}
							onClick={startEditingName}
						>
							<span className="season-setting-value-text">
								{draft.name || 'Set season name'}
							</span>
							<IonIcon icon={createOutline} />
						</button>
					)
				}
			</SeasonSettingRow>

			<SeasonSelectRow
				label="Format"
				value={draft.gameFormat || 7}
				disabled={haveRosters}
				options={[ ...seasonGameFormatOptions ]}
				onChange={value => {
					void saveField('gameFormat', value as Season['gameFormat']).catch(() => {})
				}}
			/>

			<SeasonSelectRow
				label="Game Duration"
				value={draft.numPeriods || 4}
				disabled={haveRosters}
				options={[ ...seasonNumPeriodsOptions ]}
				onChange={value => {
					void saveField('numPeriods', value as Season['numPeriods']).catch(() => {})
				}}
			/>

			<SeasonSelectRow
				label="Rules"
				value={draft.rules || 0}
				options={[ ...seasonRulesOptions ]}
				onChange={value => {
					void saveField('rules', value as Season['rules']).catch(() => {})
				}}
			/>

			<SeasonSelectRow
				label="Privacy"
				value={draft.privacy ? 1 : 0}
				options={[ ...seasonPrivacyOptions ]}
				onChange={value => {
					void saveField('privacy', !!value).catch(() => {})
				}}
			/>

			{
				showConnector && (
					<IonItem
						lines="inset"
						className="season-setting-row season-setting-row-connector"
						button={true}
						detail={false}
						onClick={() => connectorOpenRef.current?.()}
					>
						<ConnectorSelector
							value={draft.connector as Connector}
							disabled={false}
							isSeasonSettings={connectorIsCurrent}
							openRef={connectorOpenRef}
							onIonInput={connectorIsCurrent ? undefined : handleDraftConnectorInput}
						/>
					</IonItem>
				)
			}

			<div className="season-settings-close">
				{
					mode === 'add' && (
						<IonButton
							className="button-update-item button-react-icon"
							onClick={onSubmitAdd}
							disabled={!canSubmit}
						>
							{submitButtonContent || 'Submit'}
						</IonButton>
					)
				}
			</div>
		</div>
	)
}

type SeasonSettingRowProps = {
	label: string
	children: React.ReactNode
	onClick?: () => void
}

const SeasonSettingRow = (props: SeasonSettingRowProps) => (
	<IonItem
		lines="inset"
		className={`season-setting-row ${props.onClick ? 'season-setting-row-clickable' : ''}`}
		button={!!props.onClick}
		detail={false}
		onClick={props.onClick}
	>
		<IonLabel>{props.label}</IonLabel>
		<div slot="end">
			{props.children}
		</div>
	</IonItem>
)

type SeasonSelectRowProps = {
	label: string
	value: number
	options: Array<{ value: number, label?: string }>
	disabled?: boolean
	onChange: (value: number) => void
}

const SeasonSelectRow = (props: SeasonSelectRowProps) => (
	<SeasonSelectRowInner {...props} />
)

const SeasonSelectRowInner = (props: SeasonSelectRowProps) => {
	const selectRef = useRef<HTMLIonSelectElement>(null)

	return (
		<IonItem
			lines="inset"
			className={`season-setting-row ${props.disabled ? '' : 'season-setting-row-clickable'}`}
			button={!props.disabled}
			detail={false}
			onClick={() => {
				if (!props.disabled) {
					void selectRef.current?.open()
				}
			}}
		>
			<IonLabel>{props.label}</IonLabel>
			<IonSelect
				ref={selectRef}
				slot="end"
				interface="action-sheet"
				value={props.value}
				disabled={props.disabled}
				onIonChange={event => {
					props.onChange(Number(event.detail.value))
				}}
			>
				{
					props.options.map(option => (
						<IonSelectOption
							key={option.value}
							value={option.value}
						>
							{option.label ?? option.value}
						</IonSelectOption>
					))
				}
			</IonSelect>
		</IonItem>
	)
}

type ConnectorSelectorProps = {
	value: Connector
	disabled: boolean
	isSeasonSettings: boolean
	openRef?: React.MutableRefObject<(() => void) | null>
	onIonInput?: (e: { target: { value: Connector | null } }) => void
}

export const ConnectorSelector = (props: ConnectorSelectorProps) => {

	const { value, disabled, isSeasonSettings, onIonInput, openRef } = props
	const connector = value

	const [ showConnector, setShowConnector ] = useState(false)

	const [ newConnector, setNewConnector ] = useNewSeasonConnectorState()

	useEffect(() => {
		if (!isSeasonSettings && !disabled) {
			setShowConnector(false)
			onIonInput?.({
				target: {
					value: newConnector,
				},
			})
		}
	}, [ isSeasonSettings, disabled, newConnector, onIonInput ])

	useEffect(() => {
		return () => setNewConnector(null)
	}, [ setNewConnector ])

	useEffect(() => {
		if (openRef) {
			openRef.current = () => setShowConnector(true)
			return () => {
				openRef.current = null
			}
		}
		return undefined
	}, [ openRef ])

	return (
		<>
			<div className="connector-sync">
				Fixture Sync
			</div>
			<IonButton
				slot="end"
				fill="clear"
				onClick={() => setShowConnector(true)}
				className={`connector-sync-button connector-sync-button-type-${connector?.type || 'none'}`}
			>
				{
					connector?.type === 'NetballConnect' ? (
						<>
							<img className="netball-connect-image" src={netballConnectImage} alt="NetballConnect" />
							<IonIcon icon={cloudDoneOutline} className="sync-enabled" />
						</>
					) : connector?.type === 'PlayHQ' ? (
						<>
							<img className="playhq-image" src={playHqImage} alt="PlayHQ" />
							<IonIcon icon={cloudDoneOutline} className="sync-enabled" />
						</>
					) : (
						<>
							Connect...
						</>
					)
				}
			</IonButton>

			<Connector
				show={showConnector}
				selectionMode={isSeasonSettings ? 'current' : 'draft'}
				onDismiss={() => setShowConnector(false)}
			/>
		</>
	)
}

function applySeasonDefaults(season: Partial<Season>): Partial<Season> {
	return {
		gameFormat: 7,
		numPeriods: 4,
		privacy: false,
		rules: SEASON_RULE_STANDARD,
		...season,
	}
}

function applyConnectorToSeason(season: Partial<Season>, connector: Connector | null): Partial<Season> {
	const nextSeason = {
		...season,
	} as Partial<Season>

	if (connector) {
		nextSeason.connector = connector
		if (!nextSeason.name && connector.params?.compName) {
			nextSeason.name = connector.params.compName
		}
	} else {
		delete nextSeason.connector
	}

	return nextSeason
}
